/**
 * FlowKiosk — iPad-Kiosk-Hülle "/flow" (#1391).
 *
 * Öffentliche Route OHNE App-Login: 8-stelliger PIN (serverseitig bcrypt +
 * Rate-Limit + Lockout, routes/flow-kiosk.js) → eigener Kiosk-Cookie
 * `weship_kiosk` ({kiosk:true}, KEIN sub → 0 App-Zugriff, AK6) → Vollbild-
 * Voice-Bühne. Die WS-Session läuft serverseitig hart read-only (AK1,
 * voice-profile opts.kiosk).
 *
 * DESIGN (#1391, Manuels demo.html-Vorlage — bewusst NICHT das App-Theme):
 * Ink #070B14 / Ink-2 #0B1220, Akzente Teal #27B2BD / Mint #38EBBA /
 * Sky #60D4FF, Text #E8EEF6, Muted #5B6B82, Linien rgba(96,212,255,.10),
 * Font Manrope (400/600/800), FlowAvatar groß zentral (~240), Bühne mit
 * radial-gradient über Ink-2 (20px Radius), Visuals dunkel (KPI hell).
 *
 * WAKE-WORD (#1390-Kopplung): useWakeWord lauscht NUR hier (Kiosk-Kontext).
 * Standby = On-Device-Listener (kein Audio-Byte verlässt das Gerät) →
 * Wake-Wort ODER Tipp auf den Avatar → start({standby:true}) → ACTIVE mit
 * Verabschieden/Timeout → zurück in Standby (Re-Arm über state==='idle').
 *
 * Robustheit (AK3/AK4): Screen Wake Lock (re-acquire bei visibilitychange),
 * Offline-Banner + automatisches Re-Arm nach WLAN-Drop (Session-Cleanup →
 * idle → Listener startet neu), Kiosk-Token-Renew alle 60 min (401 → PIN-Gate,
 * kein weißer Screen). TTL serverseitig konfigurierbar via
 * system_config.kiosk_session_ttl_hours (Default 24 h, #1407).
 *
 * @page /flow
 * @since v2.650.0 (#1391)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useVoiceSession } from '../../hooks/useVoiceSession';
import { useWakeWord } from '../../hooks/useWakeWord';
import FlowAvatarOrb from './FlowAvatarOrb';
import VoiceVisualPanel from './VoiceVisualPanel';

const API = import.meta.env.VITE_API_URL || '';

// Palette (demo.html-Vorlage)
const INK = '#070B14';
const INK2 = '#0B1220';
const TEAL = '#27B2BD';
const MINT = '#38EBBA';
const SKY = '#60D4FF';
const TEXT = '#E8EEF6';
const MUTED = '#5B6B82';
const LINE = 'rgba(96,212,255,0.10)';

const FONT_HREF = 'https://fonts.googleapis.com/css2?family=Manrope:wght@400;600;800&display=swap';

function useManrope() {
  useEffect(() => {
    if (document.querySelector(`link[href="${FONT_HREF}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = FONT_HREF;
    document.head.appendChild(link);
  }, []);
}

// AK3 — Bildschirm wach halten (Screen Wake Lock API; iPadOS 16.4+).
function useWakeLock(active) {
  useEffect(() => {
    if (!active || !('wakeLock' in navigator)) return undefined;
    let lock = null;
    let stopped = false;
    const acquire = async () => {
      try {
        lock = await navigator.wakeLock.request('screen');
      } catch { /* z.B. Energiesparmodus — still bleiben */ }
    };
    const onVisible = () => {
      if (!stopped && document.visibilityState === 'visible') acquire();
    };
    acquire();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      document.removeEventListener('visibilitychange', onVisible);
      try { lock?.release(); } catch { /* noop */ }
    };
  }, [active]);
}

function PinGate({ t, onSuccess, pinSet }) {
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);     // {type:'invalid'|'locked', attemptsLeft?, retryAfter?}
  const [lockLeft, setLockLeft] = useState(0);

  useEffect(() => {
    if (!lockLeft) return undefined;
    const iv = setInterval(() => setLockLeft((s) => (s > 1 ? s - 1 : 0)), 1000);
    return () => clearInterval(iv);
  }, [lockLeft > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = useCallback(async (value) => {
    if (busy || value.length !== 8) return;
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(`${API}/api/flow/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pin: value }),
      });
      if (r.ok) { onSuccess(); return; }
      const body = await r.json().catch(() => ({}));
      if (r.status === 429) {
        setError({ type: 'locked' });
        setLockLeft(Number(body.retry_after_seconds) || 60);
      } else {
        setError({ type: 'invalid', attemptsLeft: body.attempts_left });
      }
      setPin('');
    } catch {
      setError({ type: 'network' });
    } finally {
      setBusy(false);
    }
  }, [busy, onSuccess]);

  const press = (d) => {
    if (busy || lockLeft > 0) return;
    setError(null);
    if (d === 'del') { setPin((p) => p.slice(0, -1)); return; }
    setPin((p) => {
      const next = (p + d).slice(0, 8);
      if (next.length === 8) submit(next);
      return next;
    });
  };

  const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'del'];

  return (
    <div className="w-full max-w-[380px] mx-auto text-center select-none">
      <div className="text-[22px] font-extrabold tracking-wide" style={{ color: TEXT }}>{t('flow.pin.title')}</div>
      <div className="mt-1 text-sm" style={{ color: MUTED }}>
        {pinSet ? t('flow.pin.prompt') : t('flow.pin.notConfigured')}
      </div>

      {/* PIN-Dots */}
      <div className="mt-7 flex justify-center gap-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <span key={i} className="inline-block w-3 h-3 rounded-full transition-colors"
            style={{ background: i < pin.length ? SKY : 'transparent', border: `1px solid ${i < pin.length ? SKY : LINE}` }} />
        ))}
      </div>

      <div className="mt-3 h-5 text-sm" style={{ color: '#F0A8A8' }}>
        {error?.type === 'invalid' && `${t('flow.pin.error')}${typeof error.attemptsLeft === 'number' ? ` ${t('flow.pin.attemptsLeft').replace('{n}', error.attemptsLeft)}` : ''}`}
        {(error?.type === 'locked' || lockLeft > 0) && t('flow.pin.locked').replace('{s}', lockLeft)}
        {error?.type === 'network' && t('flow.offline')}
      </div>

      {/* Keypad */}
      <div className="mt-4 grid grid-cols-3 gap-3">
        {keys.map((k, i) => (
          k === '' ? <span key={i} />
            : (
              <button
                key={i}
                onClick={() => press(k)}
                disabled={busy || lockLeft > 0}
                className="h-[64px] rounded-[16px] text-xl font-semibold transition-colors disabled:opacity-40"
                style={{ color: TEXT, background: INK2, border: `1px solid ${LINE}` }}
              >
                {k === 'del' ? '⌫' : k}
              </button>
            )
        ))}
      </div>
    </div>
  );
}

export default function FlowKiosk() {
  const { t } = useLanguage();
  useManrope();

  const [phase, setPhase] = useState('loading'); // loading | pin | stage | disabled
  const [pinSet, setPinSet] = useState(true);
  const [wakeCfg, setWakeCfg] = useState(null);  // {enabled, model_file, threshold, word_label}
  const [online, setOnline] = useState(navigator.onLine !== false);

  const { state, amplitude, elapsed, maxSeconds, turns, error, endReason, visual, pendingAction, actionResult, start, stop } = useVoiceSession();

  const loadStatus = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/flow/status`, { credentials: 'include' });
      if (!r.ok) throw new Error(String(r.status));
      const d = await r.json();
      setPinSet(!!d.pin_set);
      if (!d.enabled) { setPhase('disabled'); return; }
      if (d.authenticated) {
        if (d.wake) setWakeCfg(d.wake);
        setPhase('stage');
      } else {
        setPhase('pin');
      }
    } catch {
      setPhase('pin'); // Netz-Hänger → PIN-Gate statt weißem Screen (AK4)
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Online/Offline (AK4) — Banner; Re-Arm passiert über state==='idle' automatisch.
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  // Kiosk-Token-Renew (TTL = kiosk_session_ttl_hours, Default 24 h, #1407) — stündlich; 401 → PIN-Gate.
  useEffect(() => {
    if (phase !== 'stage') return undefined;
    const iv = setInterval(async () => {
      try {
        const r = await fetch(`${API}/api/flow/renew`, { method: 'POST', credentials: 'include' });
        if (r.status === 401) { stop(); setPhase('pin'); }
      } catch { /* offline — Banner zeigt es, nächster Tick versucht erneut */ }
    }, 60 * 60 * 1000);
    return () => clearInterval(iv);
  }, [phase, stop]);

  useWakeLock(phase === 'stage'); // AK3

  // Wake-Word (#1390-Kopplung): lauscht NUR auf der Kiosk-Bühne im Idle.
  const onWake = useCallback(() => { start({ standby: true }); }, [start]);
  const wakeAvailable = !!(wakeCfg?.enabled && wakeCfg?.model_file);
  const { status: wakeStatus, errorCode: wakeError } = useWakeWord({
    enabled: phase === 'stage' && wakeAvailable && state === 'idle' && online,
    modelFile: wakeCfg?.model_file,
    threshold: wakeCfg?.threshold,
    onWake,
  });
  const inStandby = wakeStatus === 'listening' || wakeStatus === 'loading';
  const wordLabel = wakeCfg?.word_label || 'Hey Flow';
  const active = state !== 'idle';

  const statusTexts = useMemo(() => ({
    idle: inStandby
      ? t('flow.standby.hint').replace('{word}', wordLabel)
      : t('flow.tapToStart'),
    connecting: t('voice.status.connecting'),
    listening: t('voice.status.listening'),
    thinking: t('voice.status.thinking'),
    speaking: t('voice.status.speaking'),
  }), [t, inStandby, wordLabel]);

  const hasOverlay = !!visual;
  const lastTurn = turns.length ? turns[turns.length - 1] : null;

  const shell = (children) => (
    <div
      className="fixed inset-0 overflow-auto flex flex-col"
      style={{ background: INK, fontFamily: "'Manrope', 'DM Sans', sans-serif", color: TEXT }}
    >
      {children}
    </div>
  );

  if (phase === 'loading') {
    return shell(
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin h-6 w-6 border-2 rounded-full" style={{ borderColor: TEAL, borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (phase === 'disabled') {
    return shell(
      <div className="flex-1 flex items-center justify-center px-8 text-center">
        <div className="text-base" style={{ color: MUTED }}>{t('flow.disabled')}</div>
      </div>
    );
  }

  if (phase === 'pin') {
    return shell(
      <div className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-[520px] rounded-[20px] px-8 py-12"
          style={{ background: `radial-gradient(120% 90% at 50% 0%, rgba(39,178,189,0.10) 0%, transparent 60%), ${INK2}`, border: `1px solid ${LINE}` }}>
          <PinGate t={t} pinSet={pinSet} onSuccess={loadStatus} />
        </div>
      </div>
    );
  }

  // ── Bühne ──
  return shell(
    <>
      {!online && (
        <div className="px-4 py-2 text-center text-sm" style={{ background: 'rgba(240,168,168,0.10)', color: '#F0A8A8' }}>
          {t('flow.offline')}
        </div>
      )}

      <div className="flex-1 flex flex-col px-5 py-6 md:px-10 md:py-8 max-w-[1180px] w-full mx-auto">
        {/* Kopf: minimal Chrome */}
        <div className="flex items-center justify-between">
          <div className="text-sm font-extrabold tracking-[2px] uppercase" style={{ color: MUTED }}>
            Flow<span style={{ color: TEAL }}>AI</span>
          </div>
          {active && (
            <div className="text-xs tabular-nums" style={{ color: MUTED }}>
              {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, '0')}{maxSeconds ? ` / ${Math.floor(maxSeconds / 60)}:${String(maxSeconds % 60).padStart(2, '0')}` : ''}
            </div>
          )}
        </div>

        {/* Bühne: Avatar zentral, Visual in voller Breite daneben/darunter (AK5) */}
        <div
          className={`mt-5 flex-1 rounded-[20px] p-6 md:p-10 flex flex-col ${hasOverlay ? 'lg:flex-row lg:items-start gap-8' : 'items-center justify-center'}`}
          style={{ background: `radial-gradient(120% 90% at 50% 0%, rgba(39,178,189,0.10) 0%, transparent 60%), ${INK2}`, border: `1px solid ${LINE}` }}
        >
          <style>{`.flow-avatar{min-height:${hasOverlay ? 200 : 320}px;width:100%}`}</style>

          <div className={`flex flex-col items-center ${hasOverlay ? 'lg:w-[300px] shrink-0' : 'w-full'}`}>
            <button
              onClick={active ? undefined : () => start({ standby: true })}
              className="w-full bg-transparent border-0 p-0 cursor-pointer"
              aria-label={t('flow.tapToStart')}
            >
              <FlowAvatarOrb state={state} amplitude={amplitude} size={hasOverlay ? 160 : 240} statusTexts={statusTexts} />
            </button>

            {/* Standby-Puls + Wake-Status */}
            {!active && (
              <div className="mt-4 flex items-center gap-2 text-sm" style={{ color: MUTED }}>
                <span className="relative flex h-2 w-2">
                  {wakeStatus === 'listening' && (
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60" style={{ background: MINT }} />
                  )}
                  <span className="relative inline-flex rounded-full h-2 w-2"
                    style={{ background: wakeStatus === 'listening' ? MINT : wakeStatus === 'error' ? '#F0A8A8' : MUTED }} />
                </span>
                {wakeStatus === 'error'
                  ? (t(`voice.wake.error.${wakeError}`) !== `voice.wake.error.${wakeError}` ? t(`voice.wake.error.${wakeError}`) : t('voice.wake.error.unknown'))
                  : inStandby
                    ? t('voice.standby.listening').replace('{word}', wordLabel)
                    : endReason ? t('voice.sessionEnded') : t('flow.tapToStart')}
              </div>
            )}

            {active && (
              <button
                onClick={stop}
                className="mt-5 px-5 py-2 rounded-[12px] text-sm font-semibold"
                style={{ color: TEXT, background: 'transparent', border: `1px solid ${LINE}` }}
              >
                {t('flow.endSession')}
              </button>
            )}

            {/* Fehler (lokalisiert, keine Provider-Details) */}
            {error && (
              <div className="mt-4 w-full max-w-[420px] text-sm rounded-[12px] px-4 py-3 text-center"
                style={{ border: '1px solid rgba(240,168,168,0.25)', background: 'rgba(240,168,168,0.06)', color: '#F0A8A8' }}>
                {t(`voice.error.${error.code}`) !== `voice.error.${error.code}` ? t(`voice.error.${error.code}`) : t('voice.error.unknown')}
              </div>
            )}

            {/* #1406 — Telegram-Vorschlag spiegeln (Confirm-Gate #1387): Empfänger +
                Text sichtbar, bis das gesprochene Ja/Nein entschieden hat. */}
            {pendingAction && (
              <div className="mt-4 w-full max-w-[460px] text-sm rounded-[12px] px-4 py-3"
                style={{ border: `1px solid ${LINE}`, background: 'rgba(125,226,209,0.06)', color: TEXT }}>
                <div className="font-semibold mb-1" style={{ color: MINT }}>{t('voice.action.pendingTitle')}</div>
                <div><span style={{ color: MUTED }}>{t('voice.action.recipient')}:</span> {pendingAction.recipient}</div>
                <div className="mt-1 leading-relaxed">{pendingAction.message}</div>
              </div>
            )}
            {actionResult && (
              <div className="mt-4 w-full max-w-[460px] text-sm rounded-[12px] px-4 py-3 text-center"
                style={{ border: `1px solid ${LINE}`, background: 'rgba(125,226,209,0.04)', color: '#A9B7C9' }}>
                {t(`voice.action.result.${actionResult.status}`) !== `voice.action.result.${actionResult.status}`
                  ? t(`voice.action.result.${actionResult.status}`)
                  : t('voice.action.result.failed')}
              </div>
            )}

            {/* Letzte Antwort dezent (kein Transcript-Log — minimal Chrome) */}
            {active && lastTurn?.role === 'assistant' && (
              <div className="mt-5 max-w-[460px] text-center text-[15px] leading-relaxed" style={{ color: '#A9B7C9' }}>
                {lastTurn.text}
              </div>
            )}
          </div>

          {hasOverlay && (
            <div className="flex-1 min-w-0 mt-8 lg:mt-0 w-full">
              <VoiceVisualPanel visual={visual} dark />
            </div>
          )}
        </div>

        {/* Datenschutz-Hinweis (AK2 — muted) */}
        <div className="mt-4 text-center text-xs leading-relaxed" style={{ color: MUTED }}>
          {wakeAvailable ? t('flow.privacy').replace('{word}', wordLabel) : t('flow.privacyManual')}
        </div>
      </div>
    </>
  );
}
